import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [invalid, setInvalid] = useState(!token);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: form.new_password,
      });
      if (data.access_token) localStorage.setItem("bc_token", data.access_token);
      if (data.user) setUser(data.user);
      toast.success("Password reset. You're signed in.");
      nav(data.user?.role === "admin" ? "/admin" : "/");
    } catch (err) {
      const detail = err.response?.data?.detail || "Reset failed";
      toast.error(detail);
      if (/expired|invalid|already been used/i.test(detail)) setInvalid(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 md:pt-40 min-h-screen max-w-[1440px] mx-auto px-6 md:px-10 pb-24 grid md:grid-cols-12 gap-10" data-testid="reset-password-page">
      <div className="md:col-span-6">
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Ch. 12 — New start</p>
        <h1 className="font-serif text-6xl md:text-8xl tracking-tight leading-none text-[#1A1714] mt-3">
          Choose a<br /><em className="font-light">new password.</em>
        </h1>
      </div>

      <div className="md:col-span-6 md:pl-10">
        {invalid ? (
          <div className="max-w-md border border-[#E5E1D8] p-8" data-testid="reset-invalid">
            <h2 className="font-serif text-3xl text-[#7A2021]">Link is invalid or expired.</h2>
            <p className="font-serif text-lg mt-3 text-[#1A1714]/70">
              Reset links are only valid for an hour and can be used once.
            </p>
            <Link
              to="/forgot-password"
              data-testid="reset-request-new"
              className="mt-8 inline-block border border-[#1A1714] px-6 py-3 font-mono text-[11px] uppercase tracking-editorial hover:bg-[#1A1714] hover:text-[#F9F6F0] transition-colors"
            >
              Request a new link →
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form" className="space-y-5 max-w-md">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">New password</label>
              <Input
                data-testid="reset-new"
                type="password"
                required
                minLength={6}
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                className="mt-1 rounded-none border-[#E5E1D8] h-12"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Confirm new password</label>
              <Input
                data-testid="reset-confirm"
                type="password"
                required
                minLength={6}
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="mt-1 rounded-none border-[#E5E1D8] h-12"
              />
            </div>
            <Button
              data-testid="reset-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial h-12"
            >
              {loading ? "Updating…" : "Reset password →"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
